import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, ArrowLeft, FileQuestion } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WhatsAppButton } from "../components/WhatsAppButton";
import { useSeo } from "../hooks/useSeo";
import api from "../lib/api";

export default function PageView() {
  const location = useLocation();
  const path = location.pathname.replace(/^\//, "");
  const [page, setPage] = useState(undefined); // undefined=loading, null=not found

  const metaDesc = page
    ? (page.body || "").replace(/[#*_`>\-\[\]!()]/g, "").replace(/\s+/g, " ").trim().slice(0, 155)
    : "";
  useSeo({
    title: page ? `${page.title} | R.O.K Motors` : (page === null ? "Page Not Found | R.O.K Motors" : undefined),
    description: page ? metaDesc : undefined,
    path: `/${path}`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    setPage(undefined);
    api
      .get(`/pages/by-path/${path}`)
      .then(({ data }) => setPage(data))
      .catch(() => setPage(null));
    let vid = localStorage.getItem("rok_visitor_id");
    if (vid) api.post("/analytics/visit", { visitor_id: vid, path: `/${path}` }).catch(() => {});
  }, [path]);

  return (
    <div className="App min-h-screen bg-white" data-testid="page-view">
      <Navbar solid />
      <main className="mx-auto min-h-[60vh] max-w-3xl px-4 pb-24 pt-32 sm:px-6 lg:px-8">
        {page === undefined ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>
        ) : page === null ? (
          <div className="py-16 text-center" data-testid="page-not-found">
            <FileQuestion className="mx-auto h-14 w-14 text-gold" />
            <h1 className="mt-6 font-display text-3xl font-black text-navy">Page not found</h1>
            <p className="mt-3 text-muted-foreground">The page you're looking for doesn't exist or isn't published.</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-brandred px-6 py-3 text-sm font-bold text-white hover:bg-brandred-700">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        ) : (
          <article data-testid="page-content">
            <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-brandred hover:underline">
              <ArrowLeft size={16} /> Back to Home
            </Link>
            <h1 className="font-display text-4xl font-black tracking-tight text-navy">{page.title}</h1>
            <div className="page-content mt-8">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.body || ""}</ReactMarkdown>
            </div>
          </article>
        )}
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
